import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Tabs,
  Tab,
  Autocomplete,
  TextField,
} from "@mui/material";
import { useTranslation } from "i18n-fe";
import { useNavigate, useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import PageBreadcrumbs from "../components/PageBreadcrumbs";
import { useUploadResults } from "../hooks/useUploadResults";
import { useMergedPreview } from "../hooks/useMergedPreview";
import { STATUS as ResultGroupStatus } from "manager-common";
import { useAuth } from "react-oidc-context";

/** USEF section codes commonly used for classes that merge technical + quality marks. */
const SUGGESTED_MERGE_SECTION_CODES = ["1562", "1583", "1579", "1580", "9980"];

function isSuggestedMergeSection(group, suggestedSectionCodes) {
  const code = group.USEFSectionCode ? String(group.USEFSectionCode).trim() : null;
  return Boolean(code && suggestedSectionCodes.includes(code));
}

function compareGroupsByClassId(a, b) {
  return String(a.classId).localeCompare(String(b.classId), undefined, { numeric: true });
}

/**
 * Merge dropdown options: all eligible groups, with suggested section codes first,
 * then other classes, each bucket sorted by class id.
 */
function buildMergeClassOptions(sortedGroups, {
  mergedClassIds,
  excludeOtherSelectionId,
  suggestedSectionCodes,
}) {
  const base = sortedGroups.filter((group) => {
    if (
      group.status === ResultGroupStatus.SKIPPED ||
      group.className === "(Did not Fill)"
    ) {
      return false;
    }
    if (excludeOtherSelectionId && group.id === excludeOtherSelectionId) {
      return false;
    }
    if (mergedClassIds.has(group.id)) {
      return false;
    }
    return true;
  });

  const suggested = base
    .filter((g) => isSuggestedMergeSection(g, suggestedSectionCodes))
    .sort(compareGroupsByClassId);
  const other = base
    .filter((g) => !isSuggestedMergeSection(g, suggestedSectionCodes))
    .sort(compareGroupsByClassId);
  return [...suggested, ...other];
}

/**
 * Only show grouped list headers when both suggested and non-suggested options exist;
 * otherwise a single flat list avoids redundant section labels.
 */
function mergeOptionsShouldUseGroupHeaders(options, suggestedSectionCodes) {
  if (options.length === 0) {
    return false;
  }
  let sawSuggested = false;
  let sawOther = false;
  for (const opt of options) {
    if (isSuggestedMergeSection(opt, suggestedSectionCodes)) {
      sawSuggested = true;
    } else {
      sawOther = true;
    }
    if (sawSuggested && sawOther) {
      return true;
    }
  }
  return false;
}

function UploadResultsPage() {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [selectedFile, setSelectedFile] = useState(null);
  const [showForm, setShowForm] = useState(true);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [currentTab, setCurrentTab] = useState(0);
  const [technicalAssessment, setTechnicalAssessment] = useState(null);
  const [qualityMarking, setQualityMarking] = useState(null);
  const [judges, setJudges] = useState([]);
  const [judgePositions, setJudgePositions] = useState({});
  const [judgePositionErrors, setJudgePositionErrors] = useState(new Set());
  const [mergedClasses, setMergedClasses] = useState([]);
  const [deletedMergeClassIds, setDeletedMergeClassIds] = useState(new Set());
  const [mergedPreviewResults, setMergedPreviewResults] = useState([]);

  const auth = useAuth();
  const { uploading, error, uploadResponse, upload, publish, reset } =
    useUploadResults();
  const { fetchPreview, loading: loadingPreview } = useMergedPreview();

  const activeUploadResponse = uploadResponse;

  // Initialize selectedIds with all group IDs when uploadResponse is available
  // Exclude SKIPPED and PUBLISHED groups (only PUBLISHED should be unchecked by default)
  // NEW, UPLOADED, and ERROR groups are selected by default
  // (Users can still manually check published groups if they want to resubmit)
  useEffect(() => {
    if (activeUploadResponse?.groups) {
      const selectableGroups = activeUploadResponse.groups.filter(
        (group) =>
          group.status !== ResultGroupStatus.SKIPPED &&
          group.status !== ResultGroupStatus.PUBLISHED &&
          group.className !== "(Did not Fill)"
      );
      const allIds = new Set(selectableGroups.map((group) => group.id));
      setSelectedIds(allIds);
    }
  }, [activeUploadResponse]);

  // Populate mergedClasses from uploadResponse when groups have merge information
  useEffect(() => {
    if (!activeUploadResponse?.groups) {
      return;
    }

    // Reset deleted merge tracking when new upload response is received
    setDeletedMergeClassIds(new Set());

    // Find all groups with merge information
    const groupsWithMerge = activeUploadResponse.groups.filter(
      (group) => group.merge && group.merge.type === "technical"
    );

    if (groupsWithMerge.length === 0) {
      setMergedClasses([]);
      return;
    }

    // Reconstruct mergedClasses from merge data
    const reconstructedMerges = groupsWithMerge.map((techGroup) => {
      // Find the corresponding quality group
      const qualityGroup = activeUploadResponse.groups.find(
        (g) =>
          g.classId === techGroup.merge.mergeWith &&
          g.merge &&
          g.merge.type === "quality" &&
          g.merge.mergeWith === techGroup.classId
      );

      if (!qualityGroup) {
        return null;
      }

      return {
        id: `merged_${techGroup.id}_${qualityGroup.id}`,
        technicalAssessment: {
          id: techGroup.id,
          classId: techGroup.classId,
          className: techGroup.className,
        },
        qualityMarking: {
          id: qualityGroup.id,
          classId: qualityGroup.classId,
          className: qualityGroup.className,
        },
        judges: techGroup.merge.judges || [],
      };
    }).filter(Boolean); // Remove null entries

    setMergedClasses(reconstructedMerges);
  }, [activeUploadResponse?.groups]);

  // Reset showForm to true when upload fails (error exists, not uploading, no successful response)
  useEffect(() => {
    if (error && !uploading && !activeUploadResponse?.success) {
      setShowForm(true);
    }
  }, [error, uploading, activeUploadResponse]);


  // Extract judges from selected groups when both classes are selected
  useEffect(() => {
    if (!technicalAssessment || !qualityMarking || !activeUploadResponse?.groups) {
      setJudges([]);
      setJudgePositions({});
      return;
    }

    const judgeMap = new Map();

    // Get judges from technical assessment group
    const techGroup = activeUploadResponse.groups.find(
      (g) => g.id === technicalAssessment.id
    );
    const techJudges = techGroup?.judges || [];
    techJudges.forEach((judge) => {
      const judgeKey = `${judge.firstName || ""}_${judge.lastName || ""}`;
      if (!judgeMap.has(judgeKey) && (judge.firstName || judge.lastName)) {
        judgeMap.set(judgeKey, {
          id: judgeKey,
          firstName: judge.firstName || "",
          lastName: judge.lastName || "",
          USEFID: judge.USEFID || "",
        });
      }
    });

    // Get judges from quality marking group
    const qualityGroup = activeUploadResponse.groups.find(
      (g) => g.id === qualityMarking.id
    );
    const qualityJudges = qualityGroup?.judges || [];
    qualityJudges.forEach((judge) => {
      const judgeKey = `${judge.firstName || ""}_${judge.lastName || ""}`;
      if (!judgeMap.has(judgeKey) && (judge.firstName || judge.lastName)) {
        judgeMap.set(judgeKey, {
          id: judgeKey,
          firstName: judge.firstName || "",
          lastName: judge.lastName || "",
          USEFID: judge.USEFID || "",
        });
      }
    });

    const uniqueJudges = Array.from(judgeMap.values());
    setJudges(uniqueJudges);
    setJudgePositions({});
  }, [technicalAssessment, qualityMarking, activeUploadResponse?.groups]);

  // Reset form when classes change
  useEffect(() => {
    setJudgePositions({});
    setJudgePositionErrors(new Set());
  }, [technicalAssessment, qualityMarking]);

  // Clear selections if they become part of a merged class
  useEffect(() => {
    // Get all class IDs that are already in merged classes (excluding deleted ones)
    const mergedClassIds = new Set();
    mergedClasses.forEach((mergedClass) => {
      if (!deletedMergeClassIds.has(mergedClass.technicalAssessment.id)) {
        mergedClassIds.add(mergedClass.technicalAssessment.id);
      }
      if (!deletedMergeClassIds.has(mergedClass.qualityMarking.id)) {
        mergedClassIds.add(mergedClass.qualityMarking.id);
      }
    });

    // Clear technical assessment if it's in merged list
    if (technicalAssessment && mergedClassIds.has(technicalAssessment.id)) {
      setTechnicalAssessment(null);
    }

    // Clear quality marking if it's in merged list
    if (qualityMarking && mergedClassIds.has(qualityMarking.id)) {
      setQualityMarking(null);
    }
  }, [mergedClasses, deletedMergeClassIds, technicalAssessment, qualityMarking]);

  // Fetch merged preview results when both technical and quality classes are selected
  useEffect(() => {
    const fetchMergedPreview = async () => {
      if (!technicalAssessment?.id || !qualityMarking?.id) {
        setMergedPreviewResults([]);
        return;
      }

      const results = await fetchPreview(technicalAssessment.id, qualityMarking.id);
      setMergedPreviewResults(results);
    };

    fetchMergedPreview();
  }, [technicalAssessment?.id, qualityMarking?.id, fetchPreview]);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      reset();
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      return;
    }

    try {
      setShowForm(false);
      await upload(selectedFile);
    } catch (err) {
      // Error is handled by the hook
      setShowForm(true);
    }
  };

  const handleCancel = () => {
    navigate("/results");
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      return format(new Date(dateString), "MM/dd/yyyy HH:mm");
    } catch (err) {
      console.error("Error formatting date:", err);
      return dateString;
    }
  };

  // Show loading spinner while uploading or loading existing results
  if (uploading && !uploadResponse) {
    return (
      <Box>
        <PageBreadcrumbs
          items={[
            { label: t("results.title"), href: "/results" },
            { label: t("results.upload.title") },
          ]}
        />
        <Paper sx={{ p: 4, maxWidth: 800, mx: "auto", mt: 2 }}>
          <Stack spacing={3}>
            {error && (
              <Alert severity="error" onClose={reset}>
                {typeof error === "object" &&
                  (error.type === "competitionStatus" ||
                    error.type === "ownershipError") ? (
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                      {t(error.messageKey)}
                    </Typography>
                    <Typography variant="body2">
                      {t(error.descriptionKey)}
                    </Typography>
                  </Box>
                ) : (
                  error
                )}
              </Alert>
            )}
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              minHeight="200px"
              gap={2}
            >
              <CircularProgress size={48} />
              <Typography variant="body1" color="text.secondary">
                {t("results.upload.uploading")}
              </Typography>
            </Box>
          </Stack>
        </Paper>
      </Box>
    );
  }

  // Show preview after successful upload or when in republish mode
  if (activeUploadResponse?.success && activeUploadResponse.groups) {
    // Sort groups: NEW/UPLOADED/ERROR at the top, then PUBLISHED, then SKIPPED/DNF at the bottom
    const sortedGroups = [...activeUploadResponse.groups].sort((a, b) => {
      const aIsSkipped =
        a.status === ResultGroupStatus.SKIPPED ||
        a.className === "(Did not Fill)";
      const bIsSkipped =
        b.status === ResultGroupStatus.SKIPPED ||
        b.className === "(Did not Fill)";

      const aIsNewOrUploadedOrError =
        a.status === ResultGroupStatus.NEW ||
        a.status === ResultGroupStatus.UPLOADED ||
        a.status === ResultGroupStatus.ERROR;
      const bIsNewOrUploadedOrError =
        b.status === ResultGroupStatus.NEW ||
        b.status === ResultGroupStatus.UPLOADED ||
        b.status === ResultGroupStatus.ERROR;

      // SKIPPED/DNF always go to bottom
      if (aIsSkipped && !bIsSkipped) return 1;
      if (!aIsSkipped && bIsSkipped) return -1;

      // NEW/UPLOADED/ERROR go to top
      if (aIsNewOrUploadedOrError && !bIsNewOrUploadedOrError) return -1;
      if (!aIsNewOrUploadedOrError && bIsNewOrUploadedOrError) return 1;

      // If both are same type (both new/uploaded/error or both published), sort by classId
      // Try to parse as numbers for numeric sorting, fall back to string comparison
      const aClassId = a.classId || "";
      const bClassId = b.classId || "";
      const aNum = parseInt(aClassId, 10);
      const bNum = parseInt(bClassId, 10);

      if (!isNaN(aNum) && !isNaN(bNum)) {
        return aNum - bNum; // Numeric sort
      }
      return aClassId.localeCompare(bClassId); // String sort
    });

    return (
      <Box>
        <PageBreadcrumbs
          items={[
            { label: t("results.title"), href: "/results" },
            { label: t("results.upload.preview.breadcrumb") },
          ]}
        />

        <Paper sx={{ p: 4, maxWidth: 1200, mx: "auto" }}>
          <Stack spacing={3}>
            {uploading && (
              <Alert severity="info" icon={<CircularProgress size={20} />}>
                {t("results.upload.uploading")} - Results are being processed...
              </Alert>
            )}
            {error && (
              <Alert severity="error" onClose={reset}>
                {typeof error === "object" &&
                  (error.type === "competitionStatus" ||
                    error.type === "ownershipError") ? (
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                      {t(error.messageKey)}
                    </Typography>
                    <Typography variant="body2">
                      {t(error.descriptionKey)}
                    </Typography>
                  </Box>
                ) : (
                  error
                )}
              </Alert>
            )}

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
              }}
            >
              <Box>
                <Typography variant="h5" gutterBottom>
                  {t("results.upload.preview.title")}
                </Typography>
                {activeUploadResponse.show && (
                  <Box sx={{ mt: 1, mb: 1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {activeUploadResponse.show.CompName ||
                        t("results.upload.preview.noShowName")}
                    </Typography>
                    <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>
                          {t("results.upload.preview.usefCompId")}:
                        </strong>{" "}
                        {activeUploadResponse.show.USEFCompID}
                      </Typography>
                      {activeUploadResponse.show.CompYear && (
                        <Typography variant="body2" color="text.secondary">
                          <strong>
                            {t("results.upload.preview.compYear")}:
                          </strong>{" "}
                          {activeUploadResponse.show.CompYear}
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                )}
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  {t("results.upload.preview.message")}
                </Typography>
              </Box>
              <Button
                variant="contained"
                onClick={async () => {
                  if (sortedGroups.length === 0) {
                    return;
                  }
                  try {
                    // Create actions array: 'publish' for selected, 'skip' for unselected
                    // Exclude SKIPPED groups from actions (they're already skipped)
                    const actions = sortedGroups
                      .filter(
                        (group) =>
                          group.status !== ResultGroupStatus.SKIPPED &&
                          group.className !== "(Did not Fill)"
                      )
                      .map((group) => {
                        const action = {
                          id: group.id,
                          action: selectedIds.has(group.id)
                            ? "publish"
                            : "skip",
                        };

                        // Check if this group had its merge deleted
                        if (deletedMergeClassIds.has(group.id)) {
                          // Explicitly clear merge data - we'll handle this in the backend
                          // by checking if merge property exists but is explicitly set to clear
                          action.clearMerge = true;
                        } else {
                          // Check if this group is part of a merged class
                          const mergedClass = mergedClasses.find(
                            (mc) =>
                              mc.technicalAssessment.id === group.id ||
                              mc.qualityMarking.id === group.id
                          );

                          if (mergedClass) {
                            if (group.id === mergedClass.technicalAssessment.id) {
                              // This is the technical assessment class
                              action.merge = {
                                type: "technical",
                                mergeWith: mergedClass.qualityMarking.classId,
                                judges: mergedClass.judges.map((judge) => ({
                                  USEFID: judge.USEFID || "",
                                  firstName: judge.firstName || "",
                                  lastName: judge.lastName || "",
                                  position: judge.position || "",
                                })),
                              };
                            } else if (group.id === mergedClass.qualityMarking.id) {
                              // This is the quality marking class
                              action.merge = {
                                type: "quality",
                                mergeWith: mergedClass.technicalAssessment.classId,
                              };
                            }
                          }
                        }

                        return action;
                      });
                    await publish(actions);
                    // Redirect to results page for this competition
                    if (activeUploadResponse?.show?.USEFCompID && activeUploadResponse?.show?.CompYear) {
                      navigate(`/results/${activeUploadResponse.show.CompYear}/${activeUploadResponse.show.USEFCompID}`);
                    }
                  } catch (err) {
                    // Error is handled by the hook
                  }
                }}
                disabled={
                  sortedGroups.filter(
                    (g) =>
                      g.status !== ResultGroupStatus.SKIPPED &&
                      g.className !== "(Did not Fill)"
                  ).length === 0 || uploading
                }
              >
                {uploading
                  ? t("results.upload.publishing")
                  : t("results.upload.preview.publishButton")}
              </Button>
            </Box>

            <Box>
              <Tabs
                value={currentTab}
                onChange={(e, newValue) => {
                  // Prevent switching to Merge tab while uploading or loading group details
                  if (newValue === 1 && uploading) {
                    return;
                  }
                  setCurrentTab(newValue);
                }}
                sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}
              >
                <Tab label="Class Results" />
                <Tab label="Merge Classes" disabled={uploading} />
              </Tabs>

              {currentTab === 0 && (
                <Box>
                  {(() => {
                    // Get all class IDs that are in merged classes (excluding deleted ones)
                    const mergedClassIds = new Set();
                    mergedClasses.forEach((mergedClass) => {
                      if (!deletedMergeClassIds.has(mergedClass.technicalAssessment.id)) {
                        mergedClassIds.add(mergedClass.technicalAssessment.id);
                      }
                      if (!deletedMergeClassIds.has(mergedClass.qualityMarking.id)) {
                        mergedClassIds.add(mergedClass.qualityMarking.id);
                      }
                    });

                    // Filter out merged classes from the display
                    const groupsWithoutMerged = sortedGroups.filter(
                      (group) => !mergedClassIds.has(group.id)
                    );

                    // Count skipped classes
                    const skippedGroups = groupsWithoutMerged.filter(
                      (group) =>
                        group.status === ResultGroupStatus.SKIPPED ||
                        group.className === "(Did not Fill)"
                    );
                    const skippedCount = skippedGroups.length;

                    // Filter out skipped classes from the display
                    const filteredGroups = groupsWithoutMerged.filter(
                      (group) =>
                        group.status !== ResultGroupStatus.SKIPPED &&
                        group.className !== "(Did not Fill)"
                    );

                    return (
                      <>
                        <Typography variant="h6" gutterBottom>
                          Class Results ({filteredGroups.length})
                        </Typography>
                        <TableContainer component={Paper} variant="outlined">
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell padding="checkbox">
                                  <Checkbox
                                    indeterminate={
                                      selectedIds.size > 0 &&
                                      selectedIds.size < filteredGroups.length
                                    }
                                    checked={
                                      filteredGroups.length > 0 &&
                                      selectedIds.size === filteredGroups.length
                                    }
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setSelectedIds(
                                          new Set(
                                            filteredGroups.map((group) => group.id)
                                          )
                                        );
                                      } else {
                                        setSelectedIds(new Set());
                                      }
                                    }}
                                  />
                                </TableCell>
                                <TableCell>
                                  {t("results.upload.preview.columns.classId")}
                                </TableCell>
                                <TableCell>
                                  {t("results.upload.preview.columns.className")}
                                </TableCell>
                                <TableCell>
                                  {t("results.upload.preview.columns.itemCount")}
                                </TableCell>
                                <TableCell>
                                  {t("results.upload.preview.columns.uploadedAt")}
                                </TableCell>
                                <TableCell>
                                  {t("results.upload.preview.columns.status")}
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {filteredGroups.map((group) => {
                                // Treat UPLOADED and ERROR the same as NEW - show as NEW, don't grey out, keep checked
                                const isNewOrUploadedOrError =
                                  group.status === ResultGroupStatus.NEW ||
                                  group.status === ResultGroupStatus.UPLOADED ||
                                  group.status === ResultGroupStatus.ERROR;
                                return (
                                  <TableRow key={group.id}>
                                    <TableCell padding="checkbox">
                                      <Checkbox
                                        checked={selectedIds.has(group.id)}
                                        onChange={(e) => {
                                          const newSelected = new Set(selectedIds);
                                          if (e.target.checked) {
                                            newSelected.add(group.id);
                                          } else {
                                            newSelected.delete(group.id);
                                          }
                                          setSelectedIds(newSelected);
                                        }}
                                      />
                                    </TableCell>
                                    <TableCell>{group.classId}</TableCell>
                                    <TableCell>{group.className || "-"}</TableCell>
                                    <TableCell>{group.itemCount}</TableCell>
                                    <TableCell>{formatDate(group.uploadedAt)}</TableCell>
                                    <TableCell>
                                      {group.status === ResultGroupStatus.ERROR
                                        ? t("results.upload.preview.reuploadFixErrors")
                                        : isNewOrUploadedOrError
                                          ? t("results.upload.preview.new")
                                          : t("results.upload.preview.reupload")}
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                              {filteredGroups.length === 0 && (
                                <TableRow>
                                  <TableCell colSpan={6} align="center">
                                    {t("results.empty")}
                                  </TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                        {skippedCount > 0 && (
                          <Typography
                            variant="body2"
                            sx={{
                              mt: 1,
                              opacity: 0.5,
                              fontStyle: "italic",
                            }}
                          >
                            {skippedCount === 1
                              ? t("results.skippedClasses.single", { count: skippedCount })
                              : t("results.skippedClasses.plural", { count: skippedCount })}
                          </Typography>
                        )}
                      </>
                    );
                  })()}
                </Box>
              )}

              {currentTab === 1 && (
                <Box>
                  <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      Merge Classes
                    </Typography>
                    <Stack spacing={3} sx={{ mt: 2 }}>
                      {(() => {
                        // Get all class IDs that are already in merged classes (excluding deleted ones)
                        const mergedClassIds = new Set();
                        mergedClasses.forEach((mergedClass) => {
                          if (!deletedMergeClassIds.has(mergedClass.technicalAssessment.id)) {
                            mergedClassIds.add(mergedClass.technicalAssessment.id);
                          }
                          if (!deletedMergeClassIds.has(mergedClass.qualityMarking.id)) {
                            mergedClassIds.add(mergedClass.qualityMarking.id);
                          }
                        });

                        const technicalOptions = buildMergeClassOptions(sortedGroups, {
                          mergedClassIds,
                          excludeOtherSelectionId: qualityMarking?.id,
                          suggestedSectionCodes: SUGGESTED_MERGE_SECTION_CODES,
                        });

                        const qualityOptions = buildMergeClassOptions(sortedGroups, {
                          mergedClassIds,
                          excludeOtherSelectionId: technicalAssessment?.id,
                          suggestedSectionCodes: SUGGESTED_MERGE_SECTION_CODES,
                        });

                        const mergeAutocompleteGroupBy = (option) =>
                          isSuggestedMergeSection(option, SUGGESTED_MERGE_SECTION_CODES)
                            ? t("results.upload.merge.suggestedGroup")
                            : t("results.upload.merge.otherGroup");

                        const technicalUseMergeGroups = mergeOptionsShouldUseGroupHeaders(
                          technicalOptions,
                          SUGGESTED_MERGE_SECTION_CODES
                        );
                        const qualityUseMergeGroups = mergeOptionsShouldUseGroupHeaders(
                          qualityOptions,
                          SUGGESTED_MERGE_SECTION_CODES
                        );

                        return (
                          <>
                            <Autocomplete
                              options={technicalOptions}
                              groupBy={
                                technicalUseMergeGroups ? mergeAutocompleteGroupBy : undefined
                              }
                              getOptionLabel={(option) =>
                                `${option.classId} - ${option.className || "-"}`
                              }
                              value={technicalAssessment}
                              onChange={(event, newValue) => {
                                // Prevent selecting a class that's in the merged list
                                if (newValue && mergedClassIds.has(newValue.id)) {
                                  return;
                                }
                                setTechnicalAssessment(newValue);
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Technical Assessment"
                                  placeholder="Select a class result group"
                                />
                              )}
                              isOptionEqualToValue={(option, value) =>
                                option?.id === value?.id
                              }
                            />
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                              <Button
                                type="button"
                                variant="outlined"
                                size="small"
                                startIcon={<SwapHorizIcon />}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  if (technicalAssessment && qualityMarking) {
                                    // Swap the selections
                                    const temp = technicalAssessment;
                                    setTechnicalAssessment(qualityMarking);
                                    setQualityMarking(temp);
                                  }
                                }}
                                disabled={!technicalAssessment || !qualityMarking}
                                sx={{ minWidth: "auto" }}
                              >
                                Swap
                              </Button>
                            </Box>
                            <Autocomplete
                              options={qualityOptions}
                              groupBy={
                                qualityUseMergeGroups ? mergeAutocompleteGroupBy : undefined
                              }
                              getOptionLabel={(option) =>
                                `${option.classId} - ${option.className || "-"}`
                              }
                              value={qualityMarking}
                              onChange={(event, newValue) => {
                                // Prevent selecting a class that's in the merged list
                                if (newValue && mergedClassIds.has(newValue.id)) {
                                  return;
                                }
                                setQualityMarking(newValue);
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Quality Marking"
                                  placeholder="Select a class result group"
                                />
                              )}
                              isOptionEqualToValue={(option, value) =>
                                option?.id === value?.id
                              }
                            />
                          </>
                        );
                      })()}
                    </Stack>

                    {/* Judges section - only show when both classes are selected */}
                    {technicalAssessment && qualityMarking && (
                      <Box sx={{ mt: 4 }}>
                        <Typography variant="h6" gutterBottom>
                          Judges
                        </Typography>
                        <Stack spacing={2} sx={{ mt: 2 }}>
                          {judges.map((judge) => (
                            <Box
                              key={judge.id}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                              }}
                            >
                              <Typography
                                variant="body1"
                                sx={{ minWidth: 200 }}
                              >
                                {judge.firstName} {judge.lastName}
                              </Typography>
                              <TextField
                                label="Position"
                                required
                                value={judgePositions[judge.id] || ""}
                                onChange={(e) => {
                                  const value = e.target.value.slice(0, 2);
                                  setJudgePositions((prev) => ({
                                    ...prev,
                                    [judge.id]: value,
                                  }));
                                  // Clear error when user starts typing
                                  if (judgePositionErrors.has(judge.id)) {
                                    setJudgePositionErrors((prev) => {
                                      const newSet = new Set(prev);
                                      newSet.delete(judge.id);
                                      return newSet;
                                    });
                                  }
                                }}
                                inputProps={{ maxLength: 2 }}
                                sx={{ width: 120 }}
                                error={judgePositionErrors.has(judge.id)}
                              />
                            </Box>
                          ))}
                          {judges.length === 0 && (
                            <Typography variant="body2" color="text.secondary">
                              No judges found in selected classes
                            </Typography>
                          )}
                        </Stack>

                        {/* Merged Preview Results Table */}
                        {loadingPreview ? (
                          <Box sx={{ mt: 3, display: "flex", justifyContent: "center" }}>
                            <CircularProgress size={24} />
                          </Box>
                        ) : mergedPreviewResults.length > 0 ? (
                          <Box sx={{ mt: 4 }}>
                            <Typography variant="h6" gutterBottom>
                              Merged Preview Results
                            </Typography>
                            <TableContainer sx={{ mt: 2 }}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Rider</TableCell>
                                    <TableCell>Horse</TableCell>
                                    <TableCell align="right">Technical Score</TableCell>
                                    <TableCell align="right">Quality Score</TableCell>
                                    <TableCell align="right">Technical %</TableCell>
                                    <TableCell align="right">Quality %</TableCell>
                                    <TableCell align="right">Total %</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {mergedPreviewResults.map((result, index) => (
                                    <TableRow key={index}>
                                      <TableCell>
                                        {result.firstName} {result.lastName}
                                      </TableCell>
                                      <TableCell>{result.horseName || "-"}</TableCell>
                                      <TableCell align="right">
                                        {result.technicalScore !== null
                                          ? result.technicalScore.toFixed(2)
                                          : "-"}
                                      </TableCell>
                                      <TableCell align="right">
                                        {result.qualityScore !== null
                                          ? result.qualityScore.toFixed(2)
                                          : "-"}
                                      </TableCell>
                                      <TableCell align="right">
                                        {result.technicalPercentage !== null
                                          ? result.technicalPercentage.toFixed(2)
                                          : "-"}
                                      </TableCell>
                                      <TableCell align="right">
                                        {result.qualityPercentage !== null
                                          ? result.qualityPercentage.toFixed(2)
                                          : "-"}
                                      </TableCell>
                                      <TableCell align="right">
                                        {result.totalPercentage !== null
                                          ? result.totalPercentage.toFixed(2)
                                          : "-"}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Box>
                        ) : null}

                        <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
                          <Button
                            variant="contained"
                            onClick={() => {
                              // Find judges with missing positions
                              const missingPositionJudges = new Set();
                              judges.forEach((judge) => {
                                if (!judgePositions[judge.id] || judgePositions[judge.id].trim() === "") {
                                  missingPositionJudges.add(judge.id);
                                }
                              });

                              // Set error state for judges with missing positions
                              if (missingPositionJudges.size > 0) {
                                setJudgePositionErrors(missingPositionJudges);
                                return; // Don't merge if positions are missing
                              }

                              // Clear any previous errors
                              setJudgePositionErrors(new Set());

                              const mergedClass = {
                                id: `merged_${Date.now()}`,
                                technicalAssessment: {
                                  id: technicalAssessment.id,
                                  classId: technicalAssessment.classId,
                                  className: technicalAssessment.className,
                                },
                                qualityMarking: {
                                  id: qualityMarking.id,
                                  classId: qualityMarking.classId,
                                  className: qualityMarking.className,
                                },
                                judges: judges.map((judge) => ({
                                  ...judge,
                                  position: judgePositions[judge.id] || "",
                                })),
                              };
                              setMergedClasses((prev) => [...prev, mergedClass]);
                              // Reset form
                              setTechnicalAssessment(null);
                              setQualityMarking(null);
                              setJudges([]);
                              setJudgePositions({});
                              setJudgePositionErrors(new Set());
                            }}
                          >
                            Merge
                          </Button>
                        </Box>
                      </Box>
                    )}
                  </Paper>

                  {/* Merged classes list */}
                  {mergedClasses.length > 0 && (
                    <Paper variant="outlined" sx={{ p: 3 }}>
                      <Typography variant="h6" gutterBottom>
                        Merged Classes
                      </Typography>
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>Technical Assessment</TableCell>
                              <TableCell>Quality Marking</TableCell>
                              <TableCell>Judges</TableCell>
                              <TableCell align="right">Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {mergedClasses.map((mergedClass) => (
                              <TableRow key={mergedClass.id}>
                                <TableCell>
                                  {mergedClass.technicalAssessment.classId} -{" "}
                                  {mergedClass.technicalAssessment.className}
                                </TableCell>
                                <TableCell>
                                  {mergedClass.qualityMarking.classId} -{" "}
                                  {mergedClass.qualityMarking.className}
                                </TableCell>
                                <TableCell>
                                  {mergedClass.judges
                                    .map(
                                      (j) =>
                                        `${j.firstName} ${j.lastName} (${j.position})`
                                    )
                                    .join(", ")}
                                </TableCell>
                                <TableCell align="right">
                                  <Button
                                    size="small"
                                    color="error"
                                    startIcon={<DeleteIcon />}
                                    onClick={() => {
                                      // Track the class IDs that had their merge deleted
                                      setDeletedMergeClassIds((prev) => {
                                        const newSet = new Set(prev);
                                        newSet.add(mergedClass.technicalAssessment.id);
                                        newSet.add(mergedClass.qualityMarking.id);
                                        return newSet;
                                      });
                                      // Remove from merged classes list
                                      setMergedClasses((prev) =>
                                        prev.filter((c) => c.id !== mergedClass.id)
                                      );
                                    }}
                                  >
                                    Delete
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Paper>
                  )}
                </Box>
              )}
            </Box>
          </Stack>
        </Paper>
      </Box>
    );
  }

  // Show form with error if upload failed
  // If there's an error and we're not uploading and no successful response, show the form
  const shouldShowForm = showForm || (error && !uploading && !activeUploadResponse?.success);

  if (!shouldShowForm) {
    return null;
  }

  return (
    <Box>
      <PageBreadcrumbs
        items={[
          { label: t("results.title"), href: "/results" },
          { label: t("results.upload.title") },
        ]}
      />

      <Paper sx={{ p: 4, maxWidth: 800, mx: "auto" }}>
        <Stack spacing={3}>
          <Box>
            <Typography variant="h5" gutterBottom>
              {t("results.upload.title")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("results.upload.description")}
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" onClose={reset}>
              {typeof error === "object" &&
                (error.type === "competitionStatus" ||
                  error.type === "ownershipError") ? (
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                    {t(error.messageKey)}
                  </Typography>
                  <Typography variant="body2">
                    {t(error.descriptionKey)}
                  </Typography>
                </Box>
              ) : (
                error
              )}
            </Alert>
          )}

          <Box>
            <Typography variant="body2" gutterBottom>
              {t("results.upload.fileLabel")}
            </Typography>
            <Box
              sx={{
                border: "2px dashed",
                borderColor: selectedFile ? "primary.main" : "grey.300",
                borderRadius: 2,
                p: 3,
                textAlign: "center",
                backgroundColor: selectedFile
                  ? "action.selected"
                  : "background.paper",
                transition: "all 0.3s ease",
                "&:hover": {
                  borderColor: "primary.main",
                  backgroundColor: "action.hover",
                },
              }}
            >
              <input
                accept=".csv,.dat"
                style={{ display: "none" }}
                id="file-upload-input"
                type="file"
                onChange={handleFileChange}
                disabled={uploading || !showForm}
              />
              <label htmlFor="file-upload-input">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUploadIcon />}
                  disabled={uploading}
                  sx={{ mb: 2 }}
                >
                  {t("results.upload.selectFile")}
                </Button>
              </label>
              {selectedFile && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="primary">
                    <UploadFileIcon sx={{ verticalAlign: "middle", mr: 1 }} />
                    {selectedFile.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {(selectedFile.size / 1024).toFixed(2)} KB
                  </Typography>
                </Box>
              )}
              {!selectedFile && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  {t("results.upload.fileHelper")}
                </Typography>
              )}
            </Box>
          </Box>

          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handleCancel}
              disabled={uploading}
            >
              {t("results.upload.cancel")}
            </Button>
            <Button
              variant="contained"
              startIcon={uploading ? null : <UploadFileIcon />}
              onClick={handleUpload}
              disabled={uploading || !selectedFile}
            >
              {uploading ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={16} />
                  {t("results.upload.uploading")}
                </Box>
              ) : (
                t("results.upload.uploadButton")
              )}
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
}

export default UploadResultsPage;
